import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Card, Dropdown, Spinner } from "react-bootstrap";
import { Empty, Input } from "antd";
import { toast } from "react-toastify";
import IconButton from "components/common/IconButton";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useCancellationPolicyRules from "hooks/cancellation-policy/useCancellationPolicyRules";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import CancellationPolicyFormModal from "./CancellationPolicyFormModal";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const statusBadge = (active) => (active ? "success" : "secondary");

const CancellationPolicyHistoryPage = () => {
  const { loading, getAllRules, createRule, updateRule, deleteRule } =
    useCancellationPolicyRules();

  const [rules, setRules] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const [showDelete, setShowDelete] = useState(false);
  const [deletingRuleId, setDeletingRuleId] = useState(null);
  const [mutating, setMutating] = useState(false);

  const loadAllRules = useCallback(async () => {
    try {
      const data = await getAllRules();
      setRules(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load cancellation rule history");
    }
  }, [getAllRules]);

  useEffect(() => {
    loadAllRules();
  }, [loadAllRules]);

  const filteredRules = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) return rules;

    return rules.filter((rule) => {
      const bucket = [
        rule?.id,
        rule?.stage,
        rule?.canceler,
        rule?.chargeBase,
        rule?.description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return bucket.includes(keyword);
    });
  }, [rules, searchTerm]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "stage",
        header: "Stage",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        accessorKey: "canceler",
        header: "Canceler",
        cell: ({ getValue }) => getValue() || "-",
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (row) => (row?.active ? "ACTIVE" : "INACTIVE"),
        cell: ({ row }) => (
          <Badge bg={statusBadge(row.original?.active)}>
            {row.original?.active ? "ACTIVE" : "INACTIVE"}
          </Badge>
        ),
      },
      {
        id: "charge",
        header: "Charge",
        accessorFn: (row) =>
          `${row?.chargePercent ?? 0}% + ${row?.chargeFixedAmount ?? 0}`,
        cell: ({ getValue }) => getValue(),
      },
      {
        accessorKey: "effectiveFrom",
        header: "Effective From",
        cell: ({ getValue }) => formatDateTime(getValue()),
      },
      {
        accessorKey: "effectiveTo",
        header: "Effective To",
        cell: ({ getValue }) => formatDateTime(getValue()),
      },
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ getValue }) => (
          <span className="font-monospace" style={{ fontSize: 12 }}>
            {getValue() || "-"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <Dropdown className="action-dropdown p-0">
            <Dropdown.Toggle
              as="button"
              className="bg-transparent border-0 p-0 shadow-none text-secondary"
            >
              ⋯
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  setEditingRule(row.original);
                  setShowForm(true);
                }}
              >
                Edit
              </Dropdown.Item>
              <Dropdown.Item
                className="text-danger"
                onClick={() => {
                  setDeletingRuleId(row.original.id);
                  setShowDelete(true);
                }}
              >
                Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        ),
        meta: {
          headerProps: { className: "text-center" },
          cellProps: { className: "text-center" },
        },
      },
    ],
    [setDeletingRuleId, setEditingRule, setShowDelete, setShowForm],
  );

  const table = useAdvanceTable({
    data: filteredRules,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10,
  });

  const onCreate = async (payload) => {
    setMutating(true);
    try {
      await createRule(payload);
      toast.success("Cancellation rule created");
      setShowForm(false);
      setEditingRule(null);
      await loadAllRules();
    } catch (err) {
      toast.error(err?.message || "Failed to create rule");
    } finally {
      setMutating(false);
    }
  };

  const onUpdate = async (payload) => {
    if (!editingRule?.id) return;
    setMutating(true);
    try {
      await updateRule(editingRule.id, payload);
      toast.success("Cancellation rule updated");
      setShowForm(false);
      setEditingRule(null);
      await loadAllRules();
    } catch (err) {
      toast.error(err?.message || "Failed to update rule");
    } finally {
      setMutating(false);
    }
  };

  const onDelete = async () => {
    if (!deletingRuleId) return;
    setMutating(true);
    try {
      await deleteRule(deletingRuleId);
      toast.success("Cancellation rule deleted");
      setShowDelete(false);
      setDeletingRuleId(null);
      await loadAllRules();
    } catch (err) {
      toast.error(err?.message || "Failed to delete rule");
      throw err;
    } finally {
      setMutating(false);
    }
  };

  return (
    <>
      <WidgetSectionTitle
        className="mt-4 mb-4"
        title="Cancellation Policy History"
        icon="table"
        subtitle="Browse all cancellation rules and manage create, update, and delete operations."
      />

      <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
        <Input
          placeholder="Search by id, stage, canceler, description..."
          value={searchTerm}
          allowClear
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: 360 }}
        />

        <IconButton
          variant="falcon-default"
          size="sm"
          icon="plus"
          onClick={() => {
            setEditingRule(null);
            setShowForm(true);
          }}
        >
          <span className="ms-1">Add Rule</span>
        </IconButton>
      </div>

      <div className="p-0">
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 220 }}
          >
            <Spinner animation="border" variant="success" />
          </div>
        ) : filteredRules.length === 0 ? (
          <div className="py-5">
            <Empty description="No cancellation rules found" />
          </div>
        ) : (
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              headerClassName="bg-300 text-nowrap align-middle text-black"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                bordered: true,
                striped: true,
                className: "fs-10 mb-0 overflow-hidden",
              }}
            />
            <div className="mt-3 px-3 pb-3">
              <AdvanceTableFooter rowInfo navButtons rowsPerPageSelection />
            </div>
          </AdvanceTableProvider>
        )}
      </div>

      <CancellationPolicyFormModal
        show={showForm}
        onClose={() => {
          if (mutating) return;
          setShowForm(false);
          setEditingRule(null);
        }}
        onSubmit={editingRule ? onUpdate : onCreate}
        loading={mutating}
        initialRule={editingRule}
        title={
          editingRule ? "Edit Cancellation Rule" : "Create Cancellation Rule"
        }
      />

      <ConfirmDeleteModal
        show={showDelete}
        setShow={setShowDelete}
        onConfirm={onDelete}
        itemName="cancellation policy rule"
        loading={mutating}
      />
    </>
  );
};

export default CancellationPolicyHistoryPage;
